import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

import Button from "src/components/button";
import Actions from "src/components/actionbar";

import { processSkills } from "../../helper";
import Slider from "src/components/slider";
import { Dropdown } from "react-bootstrap";
import LinesEllipsis from "react-lines-ellipsis";
import Tooltip from "src/components/tooltip";

import InfiniteScroll from "src/components/infiniteScroll";

import { searchSkills } from "src/redux/actions/coaching/settings";
import { Skill } from "src/types/coaching/courses";
import { Checkbox } from "src/components/input";

import _ from "lodash";
import cx from "classnames";
import styles from "./selectSkills.module.scss";

interface SelectSkillsDialogProps {
  show: boolean;
  selected: Skill[];
  // eslint-disable-next-line no-unused-vars
  onFinish: (skills: Skill[]) => void;
  onClose: () => void;
}

export const SelectSkillsDialog: React.FC<SelectSkillsDialogProps> = ({ show, selected, onFinish, onClose }) => {
  const dispatch = useDispatch();

  const { loading, skills, allSkills, currentPage, nextPage, hasMoreItems } = useSelector((state: any) => {
    const skillState = _.get(state, "coaching.settings.skills");
    const loading = _.get(skillState, "loading");
    const pagination = _.get(skillState, "pagination");
    const currentPage = _.get(pagination, "currentPage", 0);
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");
    const allSkills = _.get(skillState, "items") || [];
    const skills = _.get(skillState, "searchResults") || [];

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      loading: loading,
      allSkills: allSkills,
      skills: skills,
      currentPage: currentPage,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems,
    };
  },
    _.isEqual,
  );

  const [changes, setChanges] = useState<Skill[]>([]);
  const [query, setQuery] = useState<string | null>("");
  const [showTooltip, setShowTooltip] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<string>("");

  const handleSelect = (skill) => {
    // add the skill
    const selected = changes.some(item => item.id === skill.id || item.id === skill.parent);
    
    let updated = [...changes];
    if(!selected){
      updated = [...updated, skill];
      updated = updated.filter(item => item.parent != skill.id);
    }

    updated = processSkills(allSkills, updated);
    setChanges(updated);
  };

  const handleRemove = (skill) => {
    const selected = changes.some((item) => item.id === skill.id);
    const parentSelected = changes.some((item) => item.id === skill.parent);

    let updated = [...changes];

    if(parentSelected){
      const parent = skills.find(item => item.id === skill.parent);
      if(parent){
        const children = (parent.children || []).filter(item => item.id != skill.id);
        updated = [...updated, ...children];
        updated = updated.filter(item => item.id != parent.id);
      }
    }else if(selected){
      updated = changes.filter((item) => item.id !== skill.id);
    }

    setChanges(updated);
  };

  const handleQueryChange = (query: any) => {
    setQuery(query);
  };

  const searchSkillsForPage = (query, page) => {
    const request = {
      query: query,
      parent: null,
      page: page,
      limit: 10,
    };

    dispatch(searchSkills(request));
  };

  const handleLoadMore = () => {
    if (nextPage) {
      searchSkillsForPage(query, nextPage);
    }
  };

  const handleConfirm = () => {
    const updated = processSkills(allSkills, changes);
    onFinish(updated);
    onClose();
  };

  useEffect(() => {
    if (show) {
      setChanges([...selected]);
    }
  }, [show]);

  useEffect(() => {
    if (show) {
      searchSkillsForPage(query, 0);
    }
  }, [show, query]);

  return (
    <Slider.Dialog width={"500px"} show={show} onClose={onClose}>
      <Slider.Header title="choose skills" message="Pick skills you wish to assign to the course" />
      <Slider.Body>
        <div className={styles.skillContainer}>
          {_.size(changes) > 0 && (
            <div className={styles.selectedSkills}>
              {changes.map((skill) => {
                return (
                  <Button
                    key={skill.id}
                    variant="secondary"
                    className={styles.selectedSkill}
                    onClick={() => handleRemove(skill)}
                  >
                    <Tooltip content={skill.name} placement="top" disabled={!showTooltip}>
                    <LinesEllipsis text={skill.name || ""}
                    maxLine='1' basedOn="letters" onReflow={(({ clamped }) => {
                      setShowTooltip(clamped);
                    })} />
                  </Tooltip>
                    <i className="paperflite-close" />
                  </Button>
                );
              })}
            </div>
          )}

          <div className={styles.inputContainer}>
            <Actions.Search query={query} onSearch={handleQueryChange} />
          </div>

          <InfiniteScroll
            className={styles.skills}
            loading={loading}
            hasMore={hasMoreItems}
            loadMore={handleLoadMore}
            loader={() => {
              if (_.size(skills) === 0 || currentPage === 0) {
                return (
                  <>
                    <PlaceholderSkill width={150} />
                    <PlaceholderSkill width={220} />
                    <PlaceholderSkill width={150} />
                    <PlaceholderSkill width={175} />
                    <PlaceholderSkill width={200} />
                  </>
                );
              }
              return <PlaceholderSkill width={100} />
            }}
          >
            {_.size(skills) === 0 && !loading && (
              <span className={styles.noRecordsFound}>
                No results found
              </span>
            )}

            {skills.map((skill: any) => {
              const hasChildren = _.size(skill.children) > 0;
              if (hasChildren) {
                return (
                  <SkillDropDown
                    key={skill.id}
                    skill={skill}
                    selected={changes}
                    onSelect={handleSelect}
                    onRemove={handleRemove}
                    open={skill.id === selectedSkill}
                    selectedSkill={selectedSkill}
                    setSelectedSkill={setSelectedSkill}
                  />
                );
              } else {
                return (
                  <SkillItem
                    key={skill.id}
                    skill={skill}
                    onSelect={handleSelect}
                  />
                );
              }
            })}
          </InfiniteScroll>
        </div>
      </Slider.Body>
      <Slider.Actions>
          <Button
            icon="paperflite-tick"
            variant="primary"
            disabled={_.size(changes) === 0}
            onClick={() => handleConfirm()}
          >
            Confirm
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
      </Slider.Actions>
    </Slider.Dialog>
  );
};


const SkillItem: React.FC<any> = ({ skill, onSelect }) => {

  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <Tooltip content={skill.name} placement="top" disabled={!showTooltip}>
      <div onClick={() => onSelect(skill)}>
      <LinesEllipsis text={skill.name || ""} className={cx(styles.skill)}
      maxLine='1' basedOn="letters" onReflow={(({ clamped }) => {
        setShowTooltip(clamped);
      })} />
      </div>
    </Tooltip>
  );
}

const SkillDropDown: React.FC<any> = ({ skill, selected, onSelect, onRemove, open, selectedSkill, setSelectedSkill }) => {

  const handleSkillClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect(skill);
  }

  const handleSubSkillClick = (child) => {
    const isSelected = selected.some(
      (item) => item.id === child.id || item.id === child.parent,
    );

    if(isSelected){
      onRemove(child);
    }else{
      onSelect(child);
    }
  }

  const handleDropdownClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if(selectedSkill === skill.id){
      setSelectedSkill("");
    }
    else{
      setSelectedSkill(skill.id);
    }
  }

  return (
    <Dropdown
    key={skill.id}
    onClick={handleSkillClick}
    className={styles.selectDropdown}
    show={open}
    autoClose={"outside"}
    onToggle={(isOpen) => {
      if (!isOpen) {
        setSelectedSkill("");
      }
    }}
  >

    <Dropdown.Toggle variant="primary" id={`dropdown-${skill.id}`}>
      <Tooltip content={skill.name} placement="top" disabled={false}>
      <div className={styles.nameContainer}>
          <LinesEllipsis text={skill.name || ""} className={styles.skillItem}
          maxLine='1' basedOn="letters" 
          />
          </div>
          </Tooltip>
          <i className={cx("paperflite-dropdown", styles.icon)} onClick={handleDropdownClick} />
    </Dropdown.Toggle>

    <Dropdown.Menu
      onClick={(e) => e.stopPropagation()}
      popperConfig={{
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [0, 6],
            },
          },
        ],
      }}
    >
      {skill.children?.map((child) => {
        const isSelected = selected.some(
          (item) => item.id === child.id || item.id === child.parent,
        );
        return (
          <Dropdown.Item
            key={child.id}
            className={cx(styles.item)}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSubSkillClick(child)}
            }
          >
             <div>
              <Checkbox checked={isSelected} onChange={() => {
                handleSubSkillClick(child)
              }} />
            </div>
            <span>{child.name} </span>
          </Dropdown.Item>
        );
      })}
    </Dropdown.Menu>
  </Dropdown>
  );
}

interface PlaceholderSkillProps {
  width: number;
}

const PlaceholderSkill: React.FC<PlaceholderSkillProps> = ({ width }) => {
  return (
    <div className={cx(styles.skill, styles.placeholder)} >
      <span className={cx(styles.block)} style={{ width: width }}></span>
    </div>
  );
}